<template>
  <div class="page-container">
    <el-row :gutter="16">
      <!-- 左侧：字典类型 -->
      <el-col :xs="24" :sm="24" :md="10" :lg="8">
        <el-card shadow="never">
          <div slot="header" class="card-header">
            <span>字典类型</span>
            <el-button
              v-permission="['system:dict:create']"
              type="success" size="mini" icon="el-icon-plus"
              @click="openTypeDialog()"
            >新建</el-button>
          </div>

          <el-form :inline="true" size="small" style="margin-bottom:8px">
            <el-form-item>
              <el-input v-model="typeKeyword" placeholder="搜索代码/名称" clearable @keyup.enter.native="fetchTypes">
                <el-button slot="append" icon="el-icon-search" @click="fetchTypes" />
              </el-input>
            </el-form-item>
          </el-form>

          <el-table
            :data="typeList"
            v-loading="typeLoading"
            highlight-current-row
            size="small"
            border
            @row-click="selectType"
          >
            <el-table-column prop="code" label="字典代码" min-width="110" show-overflow-tooltip />
            <el-table-column prop="name" label="字典名称" min-width="100" show-overflow-tooltip />
            <el-table-column label="状态" width="60" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
                  {{ row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80" align="center">
              <template slot-scope="{ row }">
                <el-button
                  v-permission="['system:dict:update']"
                  type="primary" size="mini" @click.stop="openTypeDialog(row)">编辑</el-button>
                <el-button
                  v-permission="['system:dict:delete']"
                  type="danger" size="mini" @click.stop="deleteType(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap">
            <el-pagination
              small background
              :current-page="typePage"
              :page-size="typePageSize"
              :total="typeTotal"
              layout="prev, pager, next"
              @current-change="p => { typePage = p; fetchTypes() }"
            />
          </div>
        </el-card>
      </el-col>

      <!-- 右侧：字典项 -->
      <el-col :xs="24" :sm="24" :md="14" :lg="16">
        <el-card shadow="never">
          <div slot="header" class="card-header">
            <span>
              字典项
              <el-tag v-if="selectedType" type="primary" size="mini" style="margin-left:8px">
                {{ selectedType.name }}（{{ selectedType.code }}）
              </el-tag>
            </span>
            <el-button
              v-permission="['system:dict:create']"
              :disabled="!selectedType"
              type="success" size="mini" icon="el-icon-plus"
              @click="openItemDialog()"
            >新建</el-button>
          </div>

          <div v-if="!selectedType" class="empty-tip">
            <i class="el-icon-arrow-left" /> 请先在左侧选择一个字典类型
          </div>

          <el-table v-else :data="itemList" v-loading="itemLoading" border stripe size="small">
            <el-table-column prop="label" label="显示标签" min-width="100" />
            <el-table-column prop="value" label="数据值" min-width="80" />
            <el-table-column label="标签样式" width="90" align="center">
              <template slot-scope="{ row }">
                <el-tag v-if="row.tag_type" :type="row.tag_type || ''" size="mini">{{ row.label }}</el-tag>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <el-table-column prop="sort" label="排序" width="65" align="center" />
            <el-table-column label="状态" width="60" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
                  {{ row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" align="center">
              <template slot-scope="{ row }">
                <el-button
                  v-permission="['system:dict:update']"
                  type="primary" size="mini" @click="openItemDialog(row)">编辑</el-button>
                <el-button
                  v-permission="['system:dict:delete']"
                  type="danger" size="mini" @click="deleteItem(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <!-- 字典类型弹窗 -->
    <el-dialog :title="typeDialog.id ? '编辑字典类型' : '新增字典类型'" :visible.sync="typeDialogVisible" width="460px" @close="resetTypeForm">
      <el-form ref="typeForm" :model="typeForm" :rules="typeRules" label-width="90px" size="small">
        <el-form-item label="字典代码" prop="code">
          <el-input v-model="typeForm.code" :disabled="!!typeDialog.id" placeholder="如: member_status" />
        </el-form-item>
        <el-form-item label="字典名称" prop="name">
          <el-input v-model="typeForm.name" placeholder="请输入" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="typeForm.remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="typeForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="mini" @click="typeDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="typeSubmitting" @click="submitType">确定</el-button>
      </span>
    </el-dialog>

    <!-- 字典项弹窗 -->
    <el-dialog :title="itemDialog.id ? '编辑字典项' : '新增字典项'" :visible.sync="itemDialogVisible" width="460px" @close="resetItemForm">
      <el-form ref="itemForm" :model="itemForm" :rules="itemRules" label-width="90px" size="small">
        <el-form-item label="显示标签" prop="label">
          <el-input v-model="itemForm.label" placeholder="如: 启用" />
        </el-form-item>
        <el-form-item label="数据值" prop="value">
          <el-input v-model="itemForm.value" placeholder="如: 1" />
        </el-form-item>
        <el-form-item label="Tag样式">
          <el-select v-model="itemForm.tag_type" placeholder="不设置" clearable style="width:100%">
            <el-option label="默认" value="" />
            <el-option label="成功（绿）" value="success" />
            <el-option label="警告（橙）" value="warning" />
            <el-option label="危险（红）" value="danger" />
            <el-option label="信息（灰）" value="info" />
            <el-option label="主要（蓝）" value="primary" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="itemForm.sort" :min="0" style="width:120px" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="itemForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="mini" @click="itemDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="itemSubmitting" @click="submitItem">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getDictTypeList, createDictType, updateDictType, deleteDictType,
  getDictItemList, createDictItem, updateDictItem, deleteDictItem
} from '@/api/dict'

export default {
  name: 'Dict',
  data() {
    return {
      // 类型相关
      typeLoading: false,
      typeList: [],
      typeTotal: 0,
      typePage: 1,
      typePageSize: 20,
      typeKeyword: '',
      selectedType: null,
      // 类型弹窗
      typeDialogVisible: false,
      typeDialog: {},
      typeSubmitting: false,
      typeForm: { code: '', name: '', remark: '', status: 1 },
      typeRules: {
        code: [{ required: true, message: '请输入字典代码', trigger: 'blur' }],
        name: [{ required: true, message: '请输入字典名称', trigger: 'blur' }]
      },
      // 字典项相关
      itemLoading: false,
      itemList: [],
      // 字典项弹窗
      itemDialogVisible: false,
      itemDialog: {},
      itemSubmitting: false,
      itemForm: { dict_code: '', label: '', value: '', tag_type: '', sort: 0, status: 1 },
      itemRules: {
        label: [{ required: true, message: '请输入显示标签', trigger: 'blur' }],
        value: [{ required: true, message: '请输入数据值', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.fetchTypes()
  },
  methods: {
    // ---- 字典类型 ----
    async fetchTypes() {
      this.typeLoading = true
      try {
        const res = await getDictTypeList({ keyword: this.typeKeyword, page: this.typePage, per_page: this.typePageSize })
        this.typeList = res.data.list || []
        this.typeTotal = res.data.total || 0
      } finally {
        this.typeLoading = false
      }
    },
    selectType(row) {
      this.selectedType = row
      this.fetchItems()
    },
    openTypeDialog(row) {
      this.typeDialog = row ? { ...row } : {}
      this.typeForm = row
        ? { code: row.code, name: row.name, remark: row.remark || '', status: row.status }
        : { code: '', name: '', remark: '', status: 1 }
      this.typeDialogVisible = true
    },
    resetTypeForm() {
      this.$refs.typeForm && this.$refs.typeForm.resetFields()
    },
    async submitType() {
      try {
        await this.$refs.typeForm.validate()
        this.typeSubmitting = true
        if (this.typeDialog.id) {
          await updateDictType({ id: this.typeDialog.id, ...this.typeForm })
          this.$message.success('更新成功')
        } else {
          await createDictType(this.typeForm)
          this.$message.success('新增成功')
        }
        this.typeDialogVisible = false
        this.fetchTypes()
      } catch {} finally {
        this.typeSubmitting = false
      }
    },
    async deleteType(row) {
      try {
        await this.$confirm(`确定删除字典类型「${row.name}」及其所有字典项吗？`, '警告', { type: 'warning' })
        await deleteDictType({ id: row.id })
        this.$message.success('删除成功')
        if (this.selectedType && this.selectedType.id === row.id) {
          this.selectedType = null
          this.itemList = []
        }
        this.fetchTypes()
      } catch {}
    },

    // ---- 字典项 ----
    async fetchItems() {
      if (!this.selectedType) return
      this.itemLoading = true
      try {
        const res = await getDictItemList({ dict_code: this.selectedType.code })
        this.itemList = res.data || []
      } finally {
        this.itemLoading = false
      }
    },
    openItemDialog(row) {
      this.itemDialog = row ? { ...row } : {}
      this.itemForm = row
        ? { dict_code: row.dict_code, label: row.label, value: row.value, tag_type: row.tag_type || '', sort: row.sort || 0, status: row.status }
        : { dict_code: this.selectedType.code, label: '', value: '', tag_type: '', sort: 0, status: 1 }
      this.itemDialogVisible = true
    },
    resetItemForm() {
      this.$refs.itemForm && this.$refs.itemForm.resetFields()
    },
    async submitItem() {
      try {
        await this.$refs.itemForm.validate()
        this.itemSubmitting = true
        if (this.itemDialog.id) {
          await updateDictItem({ id: this.itemDialog.id, ...this.itemForm })
          this.$message.success('更新成功')
        } else {
          await createDictItem(this.itemForm)
          this.$message.success('新增成功')
        }
        this.itemDialogVisible = false
        this.fetchItems()
      } catch {} finally {
        this.itemSubmitting = false
      }
    },
    async deleteItem(row) {
      try {
        await this.$confirm(`确定删除字典项「${row.label}」吗？`, '提示', { type: 'warning' })
        await deleteDictItem({ id: row.id })
        this.$message.success('删除成功')
        this.fetchItems()
      } catch {}
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.pagination-wrap { margin-top: 10px; text-align: center; }
.empty-tip { color: #909399; text-align: center; padding: 40px 0; font-size: 14px; }
</style>
